// What makes two clips the same piece of MATERIAL.
//
// The pad grid and the material shelf both exist to turn "77 clips" into "13
// things you can hit", so both need one answer to "is this the same as that",
// and it must be the same answer or a Set has a different number of pads than
// it has cards.
//
// The obvious answer — the audio OBJECT, which is content-addressed and so
// position-independent by construction — is wrong, and measurably so. Live
// fades a clip's edges, and the fade depends on what the clip is next to, so
// the same four bars placed five times renders to three different objects.
// Checked on `s_added-a-synth_muhqyy9r` (2026-09-26): five placements of one
// drum loop, every musical field identical, three distinct hashes; the renders
// differ over 191 frames at the head and 157 at the tail of 307,200 — 4ms and
// 3ms of a 6.4-second clip, and nothing in between. Three pads for one loop.
//
// So identity is the DESCRIPTION, not a render of it: the clip's name (Live
// names a clip after its sample), the window into that sample, its length, and
// whether it warps and loops — everything that changes what you hear, and
// nothing that changes only which bytes came back. Position is deliberately
// absent: that is what turns forty placements of a kick into one entry.
//
// MIDI has no object to be confused by; it is what it plays.

import type { CanonicalClipState } from "@shared/sync/state.js";
import { warpSettingsKey, type WarpSettingsData } from "@shared/sync/protocol.js";

/** Match the digest/comparison precision used everywhere else in the repo. */
export function r4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}

/** The fields that decide whether two clips are the same material. */
export interface MaterialFacts {
  /** Absent ⇒ audio, matching `LaneClip` and `CanonicalClipState`. */
  kind?: "audio" | "midi";
  name: string;
  duration: number;
  startMarker?: number;
  endMarker?: number;
  looping?: boolean;
  warped?: boolean;
  /** Warped clips: Re-Pitch moves pitch with speed; the mode is audible. */
  warpMode?: number;
  /** Clip pitch, semitones and cents: two placements a fifth apart are
   * two sounds, not one pad. */
  transpose?: number;
  detune?: number;
  /** The warp-mode knobs: a Beats loop looped forward and one looped
   * back-and-forth are two sounds. */
  warpSettings?: WarpSettingsData;
  /** MIDI: a digest of the notes — what it plays IS its identity. */
  notesDigest?: string | null;
}

/**
 * The material's key. Scope it per track at the call site where two tracks
 * playing the same sample should stay two entries — they go through different
 * renders and different device chains, and are genuinely two things.
 */
export function materialKey(m: MaterialFacts): string {
  if (m.kind === "midi") return `midi|${m.notesDigest ?? "none"}|${r4(m.duration)}`;
  return [
    "audio",
    m.name,
    r4(m.duration),
    r4(m.startMarker ?? 0),
    r4(m.endMarker ?? 0),
    m.looping ? "loop" : "once",
    m.warped ? `warp${m.warpMode ?? 0}` : "flat",
    `p${r4(m.transpose ?? 0)}c${r4(m.detune ?? 0)}`,
    warpSettingsKey(m.warpSettings),
  ].join("|");
}

/**
 * A room clip, described. The shelf and the collision check both read room
 * state, and they must describe a clip the same way or one would fingerprint
 * a pair the other never grouped.
 */
export function roomClipFacts(c: CanonicalClipState): MaterialFacts {
  return {
    ...(c.clipKind === "midi" ? { kind: "midi" as const, notesDigest: c.notesDigest } : {}),
    name: c.name,
    duration: c.duration,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    looping: c.looping,
    warped: c.isWarped,
    warpMode: c.warpModeValue,
    ...(c.transpose ? { transpose: c.transpose } : {}),
    ...(c.detune ? { detune: c.detune } : {}),
    ...(c.warpSettings ? { warpSettings: c.warpSettings } : {}),
  };
}

/**
 * The material's key, told apart by VARIANT where one is known.
 *
 * `variant` is a fingerprint of the actual audio (lib/material-audio.ts),
 * resolved only for objects that collide on their description — two clips
 * claiming to be the same material while pointing at different renders. Where
 * it is absent the description stands alone, which is the right default: the
 * common cause of a collision is Live's clip-edge fades giving one loop
 * several renders, and those genuinely are one piece.
 */
export function variantKey(m: MaterialFacts, variant?: string): string {
  const base = materialKey(m);
  return variant === undefined ? base : `${base}|v:${variant}`;
}
